import type { HotkeyConfig, ValidationResult, ValidationViolation, ValidationWarning } from "../types";
import type {
  CharacterKeyShortcutCompliance,
  PrintableCharacterInfo,
  WCAGComplianceResult,
  WCAGViolation,
  WCAGWarning,
} from "../types/wcag";
import { isPrintableCharacter } from "./keyNormalizer";

const validateHotkeyCache = new Map<string, ValidationResult>();
const characterKeyShortcutCache = new Map<string, CharacterKeyShortcutCompliance>();
const printableCharacterInfoCache = new Map<string, PrintableCharacterInfo>();
const conflictDetectionCache = new Map<string, string[][]>();
const keySignatureCache = new Map<string, string>();
const wcagReportCache = new Map<string, WCAGComplianceResult>();

export function validateHotkey(config: HotkeyConfig): ValidationResult {
  const cacheKey = createHotkeyCacheKey(config);

  const cached = validateHotkeyCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const violations: ValidationViolation[] = [];
  const warnings: ValidationWarning[] = [];

  const charKeyCompliance = checkCharacterKeyShortcut(config);
  if (!charKeyCompliance.compliant) {
    violations.push({
      criterion: "2.1.4",
      level: "A",
      message: `Hotkey "${config.key}" uses printable character without compliance mechanism`,
      suggestion: charKeyCompliance.recommendation || "Add modifiers, scope, or disable capability",
      affectedHotkeys: [createHotkeyId(config)],
    });
  }

  if (!config.description || config.description.trim() === "") {
    violations.push({
      criterion: "4.1.2",
      level: "A",
      message: "Hotkey missing accessible description",
      suggestion: "Provide a clear description for screen reader users",
      affectedHotkeys: [createHotkeyId(config)],
    });
  }

  if (config.key.length === 1 && (!config.modifiers || config.modifiers.length === 0) && !config.scope) {
    warnings.push({
      message: `Single letter shortcut "${config.key}" may conflict with screen readers`,
      suggestion: "Consider adding modifiers or scoping to focused component",
      affectedHotkeys: [createHotkeyId(config)],
    });
  }

  const result: ValidationResult = {
    valid: violations.length === 0,
    violations,
    warnings,
  };

  validateHotkeyCache.set(cacheKey, result);

  return result;
}

function createHotkeyCacheKey(config: HotkeyConfig): string {
  const modifiers = (config.modifiers || []).sort().join(",");
  const scope = config.scope || "global";
  const description = config.description || "";
  return `${config.key}:${modifiers}:${scope}:${description}`;
}

export function checkCharacterKeyShortcut(config: HotkeyConfig): CharacterKeyShortcutCompliance {
  const modifiers = (config.modifiers || []).sort().join(",");
  const scope = config.scope || "";
  const cacheKey = `${config.key}:${modifiers}:${scope}`;

  const cached = characterKeyShortcutCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const isPrintableOnly = isPrintableCharacter(config.key) && (!config.modifiers || config.modifiers.length === 0);

  let result: CharacterKeyShortcutCompliance;

  if (!isPrintableOnly) {
    result = {
      isPrintableCharacterOnly: false,
      complianceMechanisms: {
        canDisable: true,
        canRemap: true,
        scopedToFocus: false,
      },
      compliant: true,
    };
  } else {
    const mechanisms = {
      canDisable: true,
      canRemap: true,
      scopedToFocus: Boolean(config.scope),
    };

    const compliant = mechanisms.canDisable || mechanisms.canRemap || mechanisms.scopedToFocus;

    result = {
      isPrintableCharacterOnly: true,
      complianceMechanisms: mechanisms,
      compliant,
      recommendation: compliant ? undefined : "Add scope, or ensure disable/remap functionality is available",
    };
  }

  characterKeyShortcutCache.set(cacheKey, result);

  return result;
}

export function getPrintableCharacterInfo(key: string): PrintableCharacterInfo {
  const cached = printableCharacterInfoCache.get(key);
  if (cached) {
    return cached;
  }

  const isPrintable = isPrintableCharacter(key);

  let category: PrintableCharacterInfo["category"];

  if (key.length === 1) {
    const code = key.charCodeAt(0);
    if (code >= 0x41 && code <= 0x5a) category = "letter";
    else if (code >= 0x61 && code <= 0x7a) category = "letter";
    else if (code >= 0x30 && code <= 0x39) category = "number";
    else if (code === 0x20) category = "whitespace";
    else if (isPrintable) category = "symbol";
    else category = "control";
  } else {
    const lowerKey = key.toLowerCase();
    if (["ctrl", "shift", "alt", "meta"].includes(lowerKey)) {
      category = "modifier";
    } else if (["space", "tab"].includes(lowerKey)) {
      category = "whitespace";
    } else {
      category = "control";
    }
  }

  const result: PrintableCharacterInfo = {
    isPrintable,
    category,
    requiresWCAGCompliance: isPrintable && category !== "modifier",
  };

  printableCharacterInfoCache.set(key, result);

  return result;
}

export function validateHotkeys(configs: HotkeyConfig[]): ValidationResult {
  const violations: ValidationViolation[] = [];
  const warnings: ValidationWarning[] = [];

  for (const config of configs) {
    const result = validateHotkey(config);
    violations.push(...result.violations);
    warnings.push(...result.warnings);
  }

  const scopeMap = new Map<string, HotkeyConfig[]>();

  for (const config of configs) {
    const scope = config.scope || "global";
    if (!scopeMap.has(scope)) {
      scopeMap.set(scope, []);
    }
    scopeMap.get(scope)!.push(config);
  }

  for (const [scope, hotkeys] of scopeMap) {
    const conflicts = findConflicts(hotkeys);

    for (const conflict of conflicts) {
      warnings.push({
        message: `Conflicting hotkeys in scope "${scope}": ${conflict.join(", ")}`,
        suggestion: "Use different keys or modifiers, or set priority",
        affectedHotkeys: conflict,
      });
    }
  }

  return {
    valid: violations.length === 0,
    violations,
    warnings,
  };
}

function findConflicts(hotkeys: HotkeyConfig[]): string[][] {
  const configSignatures = hotkeys.map(createHotkeyId).sort();
  const cacheKey = configSignatures.join("|");

  const cached = conflictDetectionCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const conflicts: string[][] = [];
  const keyMap = new Map<string, HotkeyConfig[]>();

  for (const hotkey of hotkeys) {
    const keySignature = createKeySignature(hotkey);
    if (!keyMap.has(keySignature)) {
      keyMap.set(keySignature, []);
    }
    keyMap.get(keySignature)!.push(hotkey);
  }

  for (const [, hotkeys] of keyMap) {
    if (hotkeys.length > 1) {
      conflicts.push(hotkeys.map(createHotkeyId));
    }
  }

  conflictDetectionCache.set(cacheKey, conflicts);

  return conflicts;
}

function createKeySignature(config: HotkeyConfig): string {
  const cacheKey = createHotkeyCacheKey(config);

  const cached = keySignatureCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const modifiers = (config.modifiers || []).sort().join("+");
  const result = modifiers ? `${modifiers}+${config.key}` : config.key;

  keySignatureCache.set(cacheKey, result);

  return result;
}

function createHotkeyId(config: HotkeyConfig): string {
  const signature = createKeySignature(config);
  return `${signature}:${config.description}`;
}

export const wcagValidationPerformance = {
  clearCache(): void {
    validateHotkeyCache.clear();
    characterKeyShortcutCache.clear();
    printableCharacterInfoCache.clear();
    conflictDetectionCache.clear();
    keySignatureCache.clear();
    wcagReportCache.clear();
  },

  getCacheStats(): {
    validateHotkey: number;
    characterKeyShortcut: number;
    printableCharacterInfo: number;
    conflictDetection: number;
    keySignature: number;
    wcagReport: number;
    totalMemoryEstimate: string;
  } {
    const estimate =
      validateHotkeyCache.size * 150
      + characterKeyShortcutCache.size * 80
      + printableCharacterInfoCache.size * 40
      + conflictDetectionCache.size * 100
      + keySignatureCache.size * 30
      + wcagReportCache.size * 200;

    return {
      validateHotkey: validateHotkeyCache.size,
      characterKeyShortcut: characterKeyShortcutCache.size,
      printableCharacterInfo: printableCharacterInfoCache.size,
      conflictDetection: conflictDetectionCache.size,
      keySignature: keySignatureCache.size,
      wcagReport: wcagReportCache.size,
      totalMemoryEstimate: `~${(estimate / 1024).toFixed(1)}KB`,
    };
  },

  preloadCommonValidations(): void {
    const commonPrintableKeys = ["a", "s", "c", "v", "x", "z", "n", "o", "p", "q", "w", "f", "1", "2", "3"];

    commonPrintableKeys.forEach((key) => {
      getPrintableCharacterInfo(key);
    });

    const modifierKeys = ["ctrl", "shift", "alt", "meta"];
    modifierKeys.forEach((key) => {
      getPrintableCharacterInfo(key);
    });

    const controlKeys = ["enter", "escape", "space", "tab", "backspace", "delete"];
    controlKeys.forEach((key) => {
      getPrintableCharacterInfo(key);
    });

    const noop = () => {};
    const commonHotkeys: HotkeyConfig[] = [
      { key: "c", modifiers: ["ctrl"], description: "Copy", handler: noop },
      { key: "v", modifiers: ["ctrl"], description: "Paste", handler: noop },
      { key: "x", modifiers: ["ctrl"], description: "Cut", handler: noop },
      { key: "z", modifiers: ["ctrl"], description: "Undo", handler: noop },
      { key: "s", modifiers: ["ctrl"], description: "Save", handler: noop },
      { key: "n", modifiers: ["ctrl"], description: "New", handler: noop },
      { key: "o", modifiers: ["ctrl"], description: "Open", handler: noop },
      { key: "a", description: "Single key without modifiers", handler: noop },
      { key: "f", scope: "search", description: "Find in search", handler: noop },
    ];

    commonHotkeys.forEach((config) => {
      validateHotkey(config);
      checkCharacterKeyShortcut(config);
    });
  },

  invalidateValidationCache(): void {
    validateHotkeyCache.clear();
    conflictDetectionCache.clear();
    wcagReportCache.clear();
    keySignatureCache.clear();
  },

  getValidationSummary(): {
    totalCachedValidations: number;
    totalCharacterAnalyses: number;
    totalConflictChecks: number;
    cacheHitRate: number;
  } {
    const totalValidations = validateHotkeyCache.size;
    const totalCharacterAnalyses = printableCharacterInfoCache.size + characterKeyShortcutCache.size;
    const totalConflictChecks = conflictDetectionCache.size;

    const totalOperations = totalValidations + totalCharacterAnalyses + totalConflictChecks;
    const cacheHitRate = totalOperations > 0 ? (totalOperations / (totalOperations + 100)) * 100 : 0;

    return {
      totalCachedValidations: totalValidations,
      totalCharacterAnalyses: totalCharacterAnalyses,
      totalConflictChecks: totalConflictChecks,
      cacheHitRate: Number(cacheHitRate.toFixed(1)),
    };
  },
};

export function generateWCAGReport(configs: HotkeyConfig[]): WCAGComplianceResult {
  const configSignatures = configs.map(createHotkeyCacheKey).sort();
  const cacheKey = configSignatures.join("|");

  const cached = wcagReportCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const violations: WCAGViolation[] = [];
  const warnings: WCAGWarning[] = [];
  const passed: Array<{ criterion: "2.1.4" | "4.1.2"; level: "A" | "AA" | "AAA"; description: string; passed: boolean }> = [];

  const validation = validateHotkeys(configs);

  for (const violation of validation.violations) {
    violations.push({
      criterion: violation.criterion as "2.1.4" | "4.1.2",
      level: violation.level,
      message: violation.message,
      impact: violation.criterion === "2.1.4" ? "serious" : "moderate",
      suggestion: violation.suggestion,
      context: { affectedHotkeys: violation.affectedHotkeys },
    });
  }

  for (const warning of validation.warnings) {
    warnings.push({
      criterion: "2.1.4",
      level: "A",
      message: warning.message,
      suggestion: warning.suggestion,
      context: { affectedHotkeys: warning.affectedHotkeys },
    });
  }

  if (validation.valid) {
    passed.push({
      criterion: "2.1.4" as const,
      level: "A" as const,
      description: "Character Key Shortcuts",
      passed: true,
    });
  }

  const result: WCAGComplianceResult = {
    compliant: violations.length === 0,
    level: "AA",
    violations,
    warnings,
    passed,
  };

  wcagReportCache.set(cacheKey, result);

  return result;
}
